import React from 'react';
import { getOneTimeVariantId } from '../../hooks/useDonationConfig';
import type { DonationAmount, OneTimeVariantIds } from '../../hooks/useDonationConfig';

type TFn = (key: string, fallback: string, params?: Record<string, string | number>) => string;

interface DonationModalProps {
  t: TFn;
  isOpen: boolean;
  infoOpen: boolean;
  onClose: () => void;
  onCloseInfo: () => void;
  onOpenInfo: () => void;
  showDonationToggle: boolean;
  donationMode: 'recurring' | 'one-time';
  onDonationModeChange: (mode: 'recurring' | 'one-time') => void;
  hasRecurringDonation: boolean;
  hasAnyOneTimeDonation: boolean;
  donateRecurringAmount: DonationAmount | null;
  onRecurringAmountChange: (amount: DonationAmount | null) => void;
  donateOneTimeAmount: DonationAmount | null;
  onOneTimeAmountChange: (amount: DonationAmount | null) => void;
  donateCustomAmount: string;
  onCustomAmountChange: (value: string) => void;
  donationOneTimeVariantIds: OneTimeVariantIds | null | undefined;
  donationInfoUrl?: string;
}

const DONATION_AMOUNTS = [1, 5, 10, 20, 50, 'custom'] as const;

const PRIMARY_BTN_CLASSES =
  'w-full py-2.5 bg-[#008BBC] text-white font-fredoka font-medium rounded-[14px] hover:bg-[#007aa8] transition-colors';
const CLOSE_BTN_CLASSES =
  'shrink-0 -mr-1 -mt-1 w-8 h-8 rounded-full text-black/40 hover:bg-gray-100 hover:text-black flex items-center justify-center text-xl leading-none';
const CUSTOM_INPUT_CLASSES =
  'w-full mt-2 h-10 px-3 bg-white border border-gray-200 rounded-lg font-poppins text-sm placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40 sm:h-auto sm:py-2';

function getAmountButtonClasses(isSelected: boolean, isEnabled: boolean): string {
  const base = 'py-2 rounded-lg font-poppins text-sm font-medium border-2 transition-all';
  if (!isEnabled) {
    return `${base} border-gray-200 bg-gray-50 text-black opacity-40 cursor-not-allowed`;
  }
  if (isSelected) {
    return `${base} border-[#008BBC] bg-[#e5f3f8] text-[#008BBC]`;
  }
  return `${base} border-gray-200 bg-white text-black hover:border-[#008BBC]/60`;
}

interface AmountButtonProps {
  isSelected: boolean;
  isEnabled: boolean;
  label: string;
  onClick: () => void;
}

function AmountButton({ isSelected, isEnabled, label, onClick }: AmountButtonProps) {
  return (
    <button
      type="button"
      disabled={!isEnabled}
      aria-pressed={isSelected}
      onClick={onClick}
      className={getAmountButtonClasses(isSelected, isEnabled)}
    >
      {label}
    </button>
  );
}

const DonationModal: React.FC<DonationModalProps> = ({
  t,
  isOpen,
  infoOpen,
  onClose,
  onCloseInfo,
  onOpenInfo,
  showDonationToggle,
  donationMode,
  onDonationModeChange,
  hasRecurringDonation,
  hasAnyOneTimeDonation,
  donateRecurringAmount,
  onRecurringAmountChange,
  donateOneTimeAmount,
  onOneTimeAmountChange,
  donateCustomAmount,
  onCustomAmountChange,
  donationOneTimeVariantIds,
  donationInfoUrl = 'https://miloylola.com/products/donacion-patitas-rescue',
}) => {
  const customLabel = t('step13c.donation_custom_btn', 'Otra');
  const customPlaceholder = t('step13c.donation_custom_placeholder', 'Importe en euros');

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => onCustomAmountChange(e.target.value);

  const renderRecurringButtons = () =>
    DONATION_AMOUNTS.map((amount) => {
      const isCustom = amount === 'custom';
      const isSelected = donateRecurringAmount === amount;
      const label = isCustom ? customLabel : `${amount} €/mes`;
      return (
        <AmountButton
          key={`recurring-${String(amount)}`}
          isSelected={isSelected}
          isEnabled={true}
          label={label}
          onClick={() => onRecurringAmountChange(isSelected ? null : amount)}
        />
      );
    });

  const renderOneTimeButtons = () =>
    DONATION_AMOUNTS.map((amount) => {
      const isCustom = amount === 'custom';
      const isEnabled = Boolean(getOneTimeVariantId(donationOneTimeVariantIds, amount));
      const isSelected = donateOneTimeAmount === amount;
      const label = isCustom ? customLabel : `${amount} €`;
      return (
        <AmountButton
          key={String(amount)}
          isSelected={isSelected}
          isEnabled={isEnabled}
          label={label}
          onClick={() => {
            if (!isEnabled) return;
            onOneTimeAmountChange(isSelected ? null : amount);
          }}
        />
      );
    });

  const showRecurringPanel = donationMode === 'recurring' && hasRecurringDonation;
  const showOneTimePanel = donationMode === 'one-time' && hasAnyOneTimeDonation;
  const showRecurringCustomInput = showRecurringPanel && donateRecurringAmount === 'custom';
  const showOneTimeCustomInput =
    showOneTimePanel && donateOneTimeAmount === 'custom' && Boolean(donationOneTimeVariantIds?.custom);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="donation-modal-backdrop"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="donation-modal-title"
            className="bg-white rounded-[20px] p-5 max-w-md w-full shadow-xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-start gap-2">
                <h3 id="donation-modal-title" className="font-fredoka text-lg font-semibold text-black leading-tight">
                  {t('step13c.donation_modal_title', 'Donación a Protectora de Animales 🐾')}
                </h3>
                <button
                  type="button"
                  aria-label={t('step13c.donation_info_aria', '¿Qué es la Protectora de Animales?')}
                  onClick={onOpenInfo}
                  className="mt-0.5 inline-flex min-h-7 min-w-7 shrink-0 items-center justify-center rounded-full border border-[#008BBC]/20 bg-[#E8F6FA] font-fredoka text-sm font-bold leading-none text-[#008BBC] transition-colors hover:bg-[#D7EEF5] sm:min-h-5 sm:min-w-5 sm:text-xs"
                >
                  i
                </button>
              </div>
              <button
                type="button"
                aria-label={t('step13c.donation_modal_close', 'Cerrar')}
                onClick={onClose}
                className={CLOSE_BTN_CLASSES}
              >
                ✕
              </button>
            </div>

            {showDonationToggle && (
              <div
                role="tablist"
                aria-label={t('step13c.donation_mode_label', 'Tipo de donación')}
                className="grid grid-cols-2 gap-1.5 p-1 bg-gray-100 rounded-lg"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={donationMode === 'recurring'}
                  onClick={() => onDonationModeChange('recurring')}
                  className={`py-2 rounded-md font-poppins text-sm font-medium transition-all ${
                    donationMode === 'recurring'
                      ? 'bg-white text-[#008BBC] shadow-sm'
                      : 'text-black/60 hover:text-black'
                  }`}
                >
                  {t('step13c.donation_recurring', 'Suscripción 1€/mes')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={donationMode === 'one-time'}
                  onClick={() => onDonationModeChange('one-time')}
                  className={`py-2 rounded-md font-poppins text-sm font-medium transition-all ${
                    donationMode === 'one-time' ? 'bg-white text-[#008BBC] shadow-sm' : 'text-black/60 hover:text-black'
                  }`}
                >
                  {t('step13c.donation_one_time', 'Donación única')}
                </button>
              </div>
            )}

            {showRecurringPanel && (
              <div className="space-y-2">
                <p className="font-poppins text-sm text-black/70">
                  {t('step13c.donation_recurring_amount_label', 'Cantidad mensual:')}
                </p>
                <div className="grid grid-cols-3 gap-2">{renderRecurringButtons()}</div>
                {showRecurringCustomInput && (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={donateCustomAmount}
                    onChange={handleCustomChange}
                    placeholder={customPlaceholder}
                    className={CUSTOM_INPUT_CLASSES}
                  />
                )}
                <p className="font-poppins text-xs text-black/50 mt-1">
                  {t(
                    'step13c.donation_recurring_help',
                    'Se renovará con cada pedido mensual. Puedes cancelarla en cualquier momento.',
                  )}
                </p>
              </div>
            )}

            {showOneTimePanel && (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">{renderOneTimeButtons()}</div>
                {showOneTimeCustomInput && (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={donateCustomAmount}
                    onChange={handleCustomChange}
                    placeholder={customPlaceholder}
                    className={CUSTOM_INPUT_CLASSES}
                  />
                )}
              </div>
            )}

            <button type="button" onClick={onClose} className={PRIMARY_BTN_CLASSES}>
              {t('step13c.donation_modal_done', 'Hecho')}
            </button>
          </div>
        </div>
      )}

      {infoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={onCloseInfo}
          data-testid="donation-info-modal-backdrop"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="donation-info-title"
            className="bg-white rounded-[20px] p-5 max-w-md w-full shadow-xl space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <h3 id="donation-info-title" className="font-fredoka text-lg font-semibold text-black">
                {t('step13c.donation_info_title', '¿Qué es la Protectora de Animales?')}
              </h3>
              <button
                type="button"
                aria-label={t('step13c.donation_info_close', 'Cerrar')}
                onClick={onCloseInfo}
                className={CLOSE_BTN_CLASSES}
              >
                ✕
              </button>
            </div>
            <p className="font-poppins text-sm text-black/70 leading-relaxed">
              {t(
                'step13c.donation_info_body',
                'Patitas&Rescue es nuestro refugio: rescatamos perros y gatos abandonados o maltratados, y los acompañamos con respeto, tiempo y cariño hasta que encuentran su hogar definitivo. Tu donación cubre alimentación, veterinaria y mejoras del refugio.',
              )}
            </p>
            <a
              href={donationInfoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center font-poppins text-xs text-[#008BBC] hover:underline"
            >
              {t('step13c.donation_info_link', 'Ver más sobre el refugio →')}
            </a>
            <button type="button" onClick={onCloseInfo} className={PRIMARY_BTN_CLASSES}>
              {t('step13c.donation_info_done', 'Entendido')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DonationModal;
